import { useSubscription } from '@apollo/client';
import { isNil } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { Subscriptions } from '@shared/api';
import { API } from '@shared/types/api';

export type WorkflowEntityEventHandler = (data: API.WorkflowEntitySubscription) => void;

export interface WorkflowEntityEventsRegistry {
  register(handler: WorkflowEntityEventHandler): VoidFunction;
}

export const WorkflowEntityEventsContext = createContext<WorkflowEntityEventsRegistry | null>(null);

/**
 * Subscribes onEvent to workflow entity events. Under a
 * WorkflowEntityEventsProvider the handler is registered with the provider's
 * shared subscription; without one (e.g. the referenced-in list inside the
 * card modal) it falls back to its own subscription, matching the previous
 * per-consumer behavior. The latest onEvent is always invoked — callers don't
 * need to memoize it.
 */
export function useWorkflowEntityEvent(onEvent: WorkflowEntityEventHandler) {
  const registry = useContext(WorkflowEntityEventsContext);
  const handlerRef = useRef(onEvent);
  useEffect(() => {
    handlerRef.current = onEvent;
  });

  useEffect(() => {
    if (isNil(registry)) {
      return;
    }
    return registry.register(data => handlerRef.current(data));
  }, [registry]);

  useSubscription<API.WorkflowEntitySubscription>(Subscriptions.workflow.event, {
    skip: !isNil(registry),
    onData: useCallback(({ data }) => {
      if (!isNil(data.data)) {
        handlerRef.current(data.data);
      }
    }, []),
  });
}
